import {
  ConsoleInstrumentation,
  CSPInstrumentation,
  ErrorsInstrumentation,
  initializeFaro,
  LogLevel,
  PerformanceInstrumentation,
  SessionInstrumentation,
  WebVitalsInstrumentation,
  type Faro,
} from "@grafana/faro-web-sdk";
import { apiBaseUrl } from "../api/apiConfig";
import { appVersion } from "../appVersion";
import { authService } from "../auth/services/authService";
import { sanitizeTransportItem } from "./beforeSend";
import { scrub } from "./scrub";
import type { TelemetryEventName, TelemetryEvents } from "./telemetryEvents";

type Attributes = Record<string, string | number | boolean | undefined>;

const faroUrl = import.meta.env.VITE_FARO_URL?.trim();
const environment = import.meta.env.VITE_FARO_ENVIRONMENT?.trim() || import.meta.env.MODE;

let faro: Faro | undefined;
let currentRoute = "/";
let user: { id?: string; businessId?: string; locationId?: string } = {};
const errorIds = new WeakMap<object, string>();

const toAttributes = (attributes: object) => Object.fromEntries(
  Object.entries(attributes).flatMap(([key, value]) => (value === undefined ? [] : [[key, String(value)]])),
);

// Telemetry must never break the app, so every Faro call is guarded.
function withFaro(callback: (instance: Faro) => void) {
  if (!faro) return;
  try { callback(faro); } catch { /* ignore telemetry failures */ }
}

/** Starts Faro when `VITE_FARO_URL` is configured. Call once, before rendering, so startup crashes are captured. */
export function initTelemetry() {
  if (faro || !faroUrl) return;
  try {
    faro = initializeFaro({
      url: faroUrl,
      app: { name: "froppio-app", version: appVersion, environment },
      instrumentations: [
        new ErrorsInstrumentation(),
        new WebVitalsInstrumentation(),
        new SessionInstrumentation(),
        new PerformanceInstrumentation(),
        new ConsoleInstrumentation(),
        new CSPInstrumentation(),
      ],
      sessionTracking: { enabled: true, persistent: true },
      consoleInstrumentation: {
        disabledLevels: [LogLevel.TRACE, LogLevel.DEBUG, LogLevel.LOG, LogLevel.INFO],
        // Errors are reported by boundaries and global handlers; console.error stays a log to avoid duplicates.
        consoleErrorAsLog: true,
      },
      ignoreErrors: [/ResizeObserver loop/],
      beforeSend: sanitizeTransportItem,
    });
  } catch (error) {
    faro = undefined;
    console.warn("Telemetry could not start.", error);
    return;
  }

  void loadTracing();
  const onConnectivity = () => trackEvent("app.connectivity_changed", { online: navigator.onLine });
  window.addEventListener("online", onConnectivity);
  window.addEventListener("offline", onConnectivity);
  trackEvent("app.started", {
    authProvider: authService.kind,
    online: navigator.onLine,
    standalone: window.matchMedia?.("(display-mode: standalone)").matches ?? false,
  });
}

// Browser spans for fetch calls; trace context is only propagated to our API so the backend joins the trace.
async function loadTracing() {
  try {
    const { TracingInstrumentation } = await import("@grafana/faro-web-tracing");
    const apiOrigin = new URL(apiBaseUrl, window.location.href).origin;
    const escaped = apiOrigin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    withFaro(instance => instance.instrumentations.add(new TracingInstrumentation({
      instrumentationOptions: { propagateTraceHeaderCorsUrls: [new RegExp(`^${escaped}/`)] },
    })));
  } catch (error) {
    console.warn("Telemetry tracing could not start.", error);
  }
}

export const isTelemetryEnabled = () => !!faro;

export function trackEvent<K extends TelemetryEventName>(name: K, attributes: TelemetryEvents[K]) {
  withFaro(instance => instance.api.pushEvent(name, toAttributes(attributes)));
}

/** Short identifier for one error occurrence, stable for the same error object. */
export function getErrorId(error: unknown): string {
  const create = () => Math.random().toString(36).slice(2, 8);
  if (typeof error !== "object" || error === null) return create();
  let id = errorIds.get(error);
  if (!id) errorIds.set(error, id = create());
  return id;
}

export const getTelemetrySessionId = (): string | undefined => {
  try { return faro?.api.getSession()?.id; } catch { return undefined; }
};

/** Code users can share with support to locate an error in Grafana. */
export function getSupportId(error: unknown) {
  const sessionId = getTelemetrySessionId();
  const errorId = getErrorId(error);
  return sessionId ? `${sessionId}-${errorId}` : errorId;
}

export interface ReportErrorOptions {
  source: string;
  fatal?: boolean;
  context?: Attributes;
}

export function reportError(error: unknown, { source, fatal, context = {} }: ReportErrorOptions) {
  const errorId = getErrorId(error);
  withFaro(instance => {
    const value = error instanceof Error
      ? error
      : new Error(typeof error === "string" ? scrub(error) : "A non-error value was thrown.");
    instance.api.pushError(value, {
      fatal,
      context: toAttributes({ ...context, source, errorId, route: currentRoute }),
    });
  });
  return errorId;
}

export const getTelemetryRoute = () => currentRoute;

export function setTelemetryRoute(route: string, scope: { businessId?: string; locationId?: string }) {
  currentRoute = route;
  withFaro(instance => instance.api.setView({ name: route }));
  if (scope.businessId !== user.businessId || scope.locationId !== user.locationId) {
    applyUser({ ...user, ...scope });
  }
}

export function setTelemetryUser(id: string | null) {
  if ((id ?? undefined) === user.id) return;
  applyUser(id ? { ...user, id } : {});
}

function applyUser(next: typeof user) {
  user = next;
  withFaro(instance => {
    if (!user.id) {
      instance.api.resetUser();
      return;
    }
    instance.api.setUser({
      id: user.id,
      attributes: toAttributes({ businessId: user.businessId, locationId: user.locationId }),
    });
  });
}
